import React, { useMemo, useState } from 'react';
import { Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel, Box } from '@mui/material';

/**
 * Generic pivot table: rows × columns with row/column totals.
 *
 * Headers are click-to-sort: the row-label column sorts alphabetically, every
 * year/value column and the Total column sort rows by that column's number.
 *
 * Props:
 *  - rows: [{ key, label }]
 *  - cols: [{ key, label }]
 *  - value(rowKey, colKey) => number   (raw value for the current metric)
 *  - percent: bool                      (show each cell as % of its column total)
 *  - format(n) => string                (raw-number formatter; default integer)
 *  - rowLabel: header for the first column (e.g. "Country")
 *  - sortByTotal: initial sort is Total desc (default); false starts in the given
 *    order (e.g. chronological periods) until a header is clicked.
 */
export default function PivotTable({ rows, cols, value, percent = false, format, rowLabel = '', sortByTotal = true }) {
  const fmt = format || ((n) => Math.round(n).toLocaleString('en-GB'));
  const pct = (n, d) => (d ? `${((n / d) * 100).toFixed(1)}%` : '—');

  // Sort target: '__label__' | '__total__' | a column key | null (given order).
  const [sort, setSort] = useState({ col: sortByTotal ? '__total__' : null, dir: 'desc' });

  const { matrix, colTotals, rowTotals, grand } = useMemo(() => {
    const mtx = rows.map((r) => cols.map((c) => Number(value(r.key, c.key)) || 0));
    const cTot = cols.map((_, ci) => mtx.reduce((s, row) => s + row[ci], 0));
    const rTot = mtx.map((row) => row.reduce((s, v) => s + v, 0));
    const g = cTot.reduce((s, v) => s + v, 0);
    return { matrix: mtx, colTotals: cTot, rowTotals: rTot, grand: g };
  }, [rows, cols, value]);

  const order = useMemo(() => {
    const idx = rows.map((_, i) => i);
    const { col, dir } = sort;
    if (!col) return idx; // given order (e.g. Jan…Dec)
    const mul = dir === 'asc' ? 1 : -1;
    if (col === '__label__') {
      return idx.sort((a, b) => mul * String(rows[a].label).localeCompare(String(rows[b].label), undefined, { numeric: true }));
    }
    if (col === '__total__') return idx.sort((a, b) => mul * (rowTotals[a] - rowTotals[b]));
    const ci = cols.findIndex((c) => c.key === col);
    return idx.sort((a, b) => mul * ((matrix[a][ci] || 0) - (matrix[b][ci] || 0)));
  }, [rows, cols, matrix, rowTotals, sort]);

  // Click a header: toggle direction if it's the active column, else select it
  // (text sorts ascending first, numbers descending first — the useful defaults).
  const handleSort = (col) =>
    setSort((prev) => {
      if (prev.col === col) return { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      return { col, dir: col === '__label__' ? 'asc' : 'desc' };
    });

  const sortLabel = (col, label) => (
    <TableSortLabel
      active={sort.col === col}
      direction={sort.col === col ? sort.dir : col === '__label__' ? 'asc' : 'desc'}
      onClick={() => handleSort(col)}
    >
      {label}
    </TableSortLabel>
  );

  const cell = (v, colTotal) => (percent ? pct(v, colTotal) : fmt(v));

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ '& td, & th': { fontSize: '0.8rem', whiteSpace: 'nowrap' } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }} sortDirection={sort.col === '__label__' ? sort.dir : false}>
              {sortLabel('__label__', rowLabel)}
            </TableCell>
            {cols.map((c) => (
              <TableCell key={c.key} align="right" sx={{ fontWeight: 700 }} sortDirection={sort.col === c.key ? sort.dir : false}>
                {sortLabel(c.key, c.label)}
              </TableCell>
            ))}
            <TableCell align="right" sx={{ fontWeight: 700, borderLeft: '2px solid rgba(0,0,0,0.15)' }} sortDirection={sort.col === '__total__' ? sort.dir : false}>
              {sortLabel('__total__', 'Total')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {order.map((ri) => (
            <TableRow key={rows[ri].key} hover>
              <TableCell sx={{ fontWeight: 600 }}>{rows[ri].label}</TableCell>
              {cols.map((c, ci) => (
                <TableCell key={c.key} align="right">{cell(matrix[ri][ci], colTotals[ci])}</TableCell>
              ))}
              <TableCell align="right" sx={{ fontWeight: 600, borderLeft: '2px solid rgba(0,0,0,0.15)' }}>
                {percent ? pct(rowTotals[ri], grand) : fmt(rowTotals[ri])}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableHead>
          <TableRow sx={{ borderTop: '2px solid rgba(0,0,0,0.15)' }}>
            <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
            {cols.map((c, ci) => (
              <TableCell key={c.key} align="right" sx={{ fontWeight: 700 }}>
                {percent ? '100%' : fmt(colTotals[ci])}
              </TableCell>
            ))}
            <TableCell align="right" sx={{ fontWeight: 700, borderLeft: '2px solid rgba(0,0,0,0.15)' }}>
              {percent ? '100%' : fmt(grand)}
            </TableCell>
          </TableRow>
        </TableHead>
      </Table>
    </Box>
  );
}
